import { Injectable } from '@angular/core';
import { PendingSkuInterface, PricingDetails, PricingDetailsModel, PricingModel } from '@app/modules/tf-pricing';
import { FishersciPricingInterface } from '@app/modules/tf-pricing/model/interfaces/fishersci-pricing.interface';
import { FishersciPricingApiService } from '@app/modules/tf-pricing/services/fishersci-pricing-api.service';
import { map, Observable, of, Subject } from 'rxjs';

@Injectable()
export class FishersciPricingService {

    serial: number = 0;

    pendingObservables: Record<number, PendingSkuInterface> = {};
    pricingDetailsRecord: Record<string, PricingModel> = {};

    constructor(
        private fishersciPricingApiService: FishersciPricingApiService
    ) {
    }

    preloadPricingInfo(skus: string[]): void {

        const uniqueSkus = this.filterUniqueSkus(skus);

        for (const sku of uniqueSkus) {

            this.fishersciPricingApiService.getPrice(sku).subscribe({
                next: (data) => {
                    this.processObservables(sku, data);
                },
                error: (error) => {
                    console.log(error);
                }
            })

        }

    }


    processObservables(sku: string, data: FishersciPricingInterface): void {

        const pricingDetail = this.processPricingDetails(data);

        this.pricingDetailsRecord[sku] = new PricingModel({
            catalogNumber: sku,
            pricingDetails: pricingDetail,
            pricingServiceError: pricingDetail.error
        });

        Object.entries(this.pendingObservables).forEach(([serial, pendingObservable]) => {
            if (pendingObservable.sku === sku) {
                pendingObservable.subject.next(this.pricingDetailsRecord[pendingObservable.sku]);
                pendingObservable.subject.complete();
                delete this.pendingObservables[serial];
            }
        });

    }

    processPricingDetails(pricingData: FishersciPricingInterface): PricingDetailsModel {

        const priceUoms = pricingData.priceUoms;

        let pricingDetailsModel = new PricingDetailsModel();

        if (pricingData
            && Array.isArray(pricingData.priceUoms)
            && pricingData.priceUoms.length > 0
        ) {

            const uom = priceUoms[0];

            // TODO: We may need to map more fields here
            const details: PricingDetails ={
                finalPrice: uom.itemPrice
            };

            pricingDetailsModel = new PricingDetailsModel(details);

            return pricingDetailsModel;

        }

        return pricingDetailsModel;

    }

    getPreloadedPricing(sku: string): Observable<PricingModel> {

        if (this.pricingDetailsRecord[sku]) {
            return of(this.pricingDetailsRecord[sku]);
        }

        const data: PendingSkuInterface = {
            sku: sku,
            subject: new Subject<PricingModel>()
        };

        this.pendingObservables[this.serial++] = data;

        return data.subject;

    }

    getPricing(sku: string): Observable<PricingModel> {

        if (this.pricingDetailsRecord[sku]) {
            return of(this.pricingDetailsRecord[sku]);
        }

        return this.retrievePricingInfo(sku).pipe(
            map(() => {
                return this.pricingDetailsRecord[sku];
            })
        );

    }

    retrievePricingInfo(sku: string): Observable<PricingModel> {

        const data: PendingSkuInterface = {
            sku: sku,
            subject: new Subject<PricingModel>()
        };

        this.fishersciPricingApiService.getPrice(sku).subscribe({
            next: (data) => {
                this.processObservables(sku, data);
            },
            error: (error) => {
                console.log(error);
            }
        })

        this.pendingObservables[this.serial++] = data;

        return data.subject;

    }

    private filterUniqueSkus(skus: string[]): string[] {
        return skus.filter((item, pos, self) => {
            return self.indexOf(item) == pos;
        });
    }

    getAnonymousCurrencyCode(): string {

        let currencyCode = null;

        for (const sku in this.pricingDetailsRecord) {

            if (this.pricingDetailsRecord[sku]?.pricingDetails?.pricingCurrencyCode != undefined) {
                currencyCode = this.pricingDetailsRecord[sku]?.pricingDetails?.pricingCurrencyCode;
                break;
            }

        }

        return currencyCode != null ? currencyCode : '';

    }

}
